import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { EmailClient } from '@amazon-connect/email';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData } from '../../../utils/emailContactHelpers';
import { fetchEmailBody } from '../../../services/fetchEmailBody';
import { fetchAttachment } from '../../../services/fetchAttachment';
import { EmailAttachment, EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import DOMPurify from 'dompurify';

export function filterThreadContacts(contacts: ThreadContact[], activeContactId: string): ThreadContact[] {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  return contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
}

interface UseEmailContactHandlersProps {
  onViewContact?: (contactId: string | undefined) => void;
}

export function useEmailContactHandlers({ onViewContact }: UseEmailContactHandlersProps): void {
  const {
    addEmailContact,
    removeEmailContact,
    updateEmailData,
    updateEmailThread,
    startLoadingThread,
    stopLoadingThread
  } = useEmailContext();
  const isInitializedRef = useRef(false);
  const emailClientRef = useRef<EmailClient | null>(null);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const client = emailClientRef.current;

      if (!client) return;

      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);
        const contacts = await fetchEmailThread(contactAssociationId);

        const filteredContacts = filterThreadContacts(contacts, activeContactId);

        const messages: EmailMessage[] = await Promise.all(
          filteredContacts.map(async threadContact => {
            const emailData = await client.getEmailData({
              contactId: threadContact.contactId,
              activeContactId
            });

            const { bodyLocation, attachmentLocations, subject, from, to, cc, direction, deliveredTo } = emailData;

            let bodyHtml: string | undefined;

            if (bodyLocation) {
              const rawBodyHtml = await fetchEmailBody(bodyLocation, threadContact.contactId);
              bodyHtml = rawBodyHtml ? DOMPurify.sanitize(rawBodyHtml) : undefined;
            }

            const attachmentResults = await Promise.allSettled(
              (attachmentLocations ?? []).map(async location => {
                const { fileName, contentType } = await fetchAttachment(location, threadContact.contactId);
                return {
                  fileName,
                  contentType,
                  fileId: location.fileId,
                  associatedResourceArn: location.associatedResourceArn
                };
              })
            );

            const attachments: EmailAttachment[] = attachmentResults
              .filter((r): r is PromiseFulfilledResult<EmailAttachment> => r.status === 'fulfilled')
              .map(r => r.value);

            return {
              contactId: threadContact.contactId,
              subject,
              from,
              // deliveredTo is the actual inbox address for inbound emails (e.g. the queue
              // address) and takes precedence over the generic `to` field from the SDK
              to: deliveredTo ? [deliveredTo] : to ?? [],
              cc,
              bodyHtml,
              attachments,
              timestamp: new Date(threadContact.initiationTimestamp).getTime(),
              direction
            };
          })
        ).then(msgs => msgs.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)));

        updateEmailThread(activeContactId, { contactAssociationId, messages });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      const contactId = contact.getContactId();

      addEmailContact(contactId);
      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });

      contact.onDestroy(() => {
        removeEmailContact(contactId);
        if (onViewContact) onViewContact(undefined);
      });
    };

    connect.agent(agent => {
      // getSDKClientConfig() returns a broader type than ConnectClientConfig, so cast until types are aligned upstream

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      emailClientRef.current = new EmailClient(connect.core.getSDKClientConfig() as any);

      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      handleEmailContact(contact);
    });
    }, [
    addEmailContact,
    removeEmailContact,
    updateEmailData,
    updateEmailThread,
    startLoadingThread,
    stopLoadingThread,
    onViewContact
  ]);
}
